import { Pressable, ScrollView, Text, View, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import InputSection from '@/components/General/inputs/InputSection';
import { useState, useMemo, useCallback } from 'react';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import { ActivityIndicator } from 'react-native-paper';
import Toast from 'react-native-toast-message';
import { mergeEvents } from '@/data/events';
import useLoader from '@/utils/hooks/useLoader';
import { showAlert } from '@/utils/showAlert';
import { usePathname } from 'expo-router';
import SolidButton from '@/components/General/SolidButton';
import Callout from '@/components/General/Callout';

const STEP_SELECT = 0;
const STEP_REVIEW = 1;

export default function MergeEventsScreen() {
    const [currentStep, setCurrentStep] = useState(STEP_SELECT);
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [selectedTargetLocation, setSelectedTargetLocation] = useState(null);

    const loader = useLoader();
    const pathname = usePathname();
    const queryClient = useQueryClient();

    const { data: sourceEvent, isLoading: loadingSource } = Query.events.byIdForMerge({
        id: selectedSource?.id,
        options: { enabled: selectedSource != null }
    });
    const { data: targetEvent, isLoading: loadingTarget } = Query.events.byIdForMerge({
        id: selectedTarget?.id,
        options: { enabled: selectedTarget != null }
    });

    const mergeMutation = useMutation({
        mutationFn: mergeEvents,
        onSuccess: () => {
            Toast.show({ type: 'success', text2: "Events have been merged successfully!" });
            queryClient.invalidateQueries({ queryKey: ['events'] });
            resetForm();
            loader.disable();
        },
        onError: () => {
            loader.disable();
        }
    });

    const resetForm = useCallback(() => {
        setSelectedSource(null);
        setSelectedTarget(null);
        setSelectedTargetLocation(null);
        setCurrentStep(STEP_SELECT);
    }, []);

    const sourceInstanceCount = useMemo(() => {
        return sourceEvent?.EventInstances?.length || 0;
    }, [sourceEvent]);

    const targetInstanceCount = useMemo(() => {
        return targetEvent?.EventInstances?.length || 0;
    }, [targetEvent]);

    const targetLocationsQuery = useMemo(() => {
        if (!targetEvent?.Locations) return null;
        return {
            data: targetEvent.Locations.map(el => ({
                id: el.locationID || el.Location?.id,
                name: el.Location?.name,
                detailedArea: el.Location?.detailedArea,
                Address: el.Location?.Address
            }))
        };
    }, [targetEvent]);

    const handleMerge = useCallback(() => {
        showAlert({
            title: "Confirm Merge",
            subtitle: `This will merge ${sourceInstanceCount} occurrence(s) from "${sourceEvent?.name}" into "${targetEvent?.name}" at location "${selectedTargetLocation?.name || 'default'}", then mark "${sourceEvent?.name}" as deleted. This action cannot be undone.`,
            confirmText: "Merge Events",
            onConfirm: () => {
                loader.enable();
                mergeMutation.mutate({
                    sourceId: sourceEvent.id,
                    targetId: targetEvent.id,
                    targetLocationId: selectedTargetLocation?.id,
                });
            }
        });
    }, [sourceEvent, targetEvent, sourceInstanceCount, selectedTargetLocation]);

    const canProceed = useMemo(() => {
        switch (currentStep) {
            case STEP_SELECT:
                return selectedSource && selectedTarget && selectedSource.id !== selectedTarget?.id && selectedTargetLocation && !loadingSource && !loadingTarget;
            case STEP_REVIEW:
                return true;
            default:
                return false;
        }
    }, [currentStep, selectedSource, selectedTarget, selectedTargetLocation, loadingSource, loadingTarget]);

    const isLoading = loadingSource || loadingTarget;

    const renderStepIndicator = () => (
        <View style={styles.stepIndicatorContainer}>
            {['Select', 'Review'].map((label, index) => (
                <Pressable
                    key={label}
                    style={styles.stepItem}
                    onPress={() => index < currentStep && setCurrentStep(index)}
                    disabled={index >= currentStep}
                >
                    <View style={styles.stepCircle(currentStep === index, currentStep > index)}>
                        {currentStep > index ? (
                            <Unicon name="checkmark" size={14} colorVariant="onPrimary" />
                        ) : (
                            <Text style={styles.stepNumber(currentStep === index)}>{index + 1}</Text>
                        )}
                    </View>
                    <Text style={styles.stepLabel(currentStep === index)}>{label}</Text>
                    {index < 1 && <View style={styles.stepConnector(currentStep > index)} />}
                </Pressable>
            ))}
        </View>
    );

    const renderEventCard = (event, role) => {
        if (!event) return null;
        const isSource = role === 'source';
        const instanceCount = isSource ? sourceInstanceCount : targetInstanceCount;

        return (
            <View style={styles.groupCard(isSource)}>
                <View style={styles.groupCardHeader}>
                    <View style={styles.groupCardIcon(isSource)}>
                        <Unicon name={isSource ? "trash-outline" : "checkmark-circle-outline"} size={20} colorVariant={isSource ? "error" : "success"} />
                    </View>
                    <View style={styles.groupCardInfo}>
                        <Text style={styles.groupName}>{event.name}</Text>
                        <Text style={styles.groupMeta}>{event.Calendar?.name || 'Event'}</Text>
                    </View>
                    <View style={styles.roleBadge(isSource)}>
                        <Text style={styles.roleBadgeText(isSource)}>{isSource ? 'Duplicate' : 'Keep'}</Text>
                    </View>
                </View>

                <View style={styles.groupCardDetails}>
                    <View style={styles.detailRow}>
                        <Unicon name="calendar-outline" size={14} colorVariant="textSecondary" />
                        <Text style={styles.detailText}>{instanceCount} occurrence{instanceCount !== 1 ? 's' : ''}</Text>
                    </View>
                    {event.Calendar && (
                        <View style={styles.detailRow}>
                            <View style={[styles.calendarDot, { backgroundColor: event.Calendar.color || '#999' }]} />
                            <Text style={styles.detailText}>{event.Calendar.name}</Text>
                        </View>
                    )}
                </View>

                <View style={styles.groupCardFooter}>
                    <Unicon name={isSource ? "close-circle" : "checkmark-circle"} size={18} colorVariant={isSource ? "error" : "success"} />
                    <Text style={styles.groupCardFooterText}>
                        {isSource ? "This Event will be marked as deleted after merge" : "Occurrences will be merged into this Event"}
                    </Text>
                </View>
            </View>
        );
    };

    const renderSelectStep = () => (
        <View style={styles.stepContent}>
            <Callout
                type="info"
                title="How Merge Works"
                message="Select the duplicate Event and the one you want to keep. For each occurrence of the duplicate, we'll find a matching occurrence (within 1 hour) of the target event and check everyone in. If no match exists, we'll create a new occurrence. The duplicate event will then be marked as deleted."
            />

            <InputSection
                title="Duplicate (will be deleted)"
                description="Select the Event that is a duplicate. Its occurrences will be moved."
            >
                <SheetSelector
                    type="events"
                    title="Duplicate Event"
                    fromScreen={pathname}
                    value={selectedSource}
                    excludeIds={selectedTarget ? [selectedTarget.id] : []}
                    onChange={setSelectedSource}
                />
            </InputSection>

            <InputSection
                title="Keep (merge into)"
                description="Select the Event you want to keep. Occurrences from the duplicate will be added here."
            >
                <SheetSelector
                    type="events"
                    title="Target Event"
                    fromScreen={pathname}
                    value={selectedTarget}
                    excludeIds={selectedSource ? [selectedSource.id] : []}
                    onChange={setSelectedTarget}
                />
            </InputSection>

            {targetEvent && (
                <InputSection
                    title="Location for check-ins"
                    description="Select the location where attendees from the duplicate event will be checked in."
                >
                    <SheetSelector
                        type="locations"
                        title="Target Location"
                        fromScreen={pathname}
                        value={selectedTargetLocation}
                        onChange={setSelectedTargetLocation}
                        queryResult={targetLocationsQuery}
                    />
                </InputSection>
            )}

            {selectedSource?.id === selectedTarget?.id && selectedSource && (
                <Callout type="error" message="You cannot merge an Event with itself. Please select two different Events." />
            )}

            {isLoading && (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" />
                    <Text style={styles.loadingText}>Loading Event details...</Text>
                </View>
            )}

            {sourceEvent && targetEvent && !isLoading && (
                <InputSection title="Preview">
                    {renderEventCard(sourceEvent, 'source')}
                    <View style={styles.mergeArrow}>
                        <Unicon name="arrow-down" size={24} colorVariant="primary" />
                        <Text style={styles.mergeArrowText}>{sourceInstanceCount} occurrence{sourceInstanceCount !== 1 ? 's' : ''} will move</Text>
                    </View>
                    {renderEventCard(targetEvent, 'target')}
                </InputSection>
            )}
        </View>
    );

    const renderReviewStep = () => (
        <View style={styles.stepContent}>
            <Callout
                type="warning"
                title="Review Carefully"
                message="Please review the merge summary below. This action cannot be undone."
            />

            <View style={styles.reviewSection}>
                <SectionTitle title="Event to Delete" />
                {renderEventCard(sourceEvent, 'source')}
            </View>

            <View style={styles.mergeArrow}>
                <View style={styles.mergeArrowLine} />
                <View style={styles.mergeArrowCircle}>
                    <Unicon name="git-merge-outline" size={20} colorVariant="onPrimary" />
                </View>
                <View style={styles.mergeArrowLine} />
            </View>

            <View style={styles.reviewSection}>
                <SectionTitle title="Event to Keep" />
                {renderEventCard(targetEvent, 'target')}
            </View>

            <View style={styles.summaryCard}>
                <Text style={styles.summaryTitle}>Merge Summary</Text>
                <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Occurrences to process</Text>
                    <Text style={styles.summaryValue}>{sourceInstanceCount}</Text>
                </View>
                <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Check-in location</Text>
                    <Text style={styles.summaryValue}>{selectedTargetLocation?.name || 'Default'}</Text>
                </View>
                <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Existing occurrences in target</Text>
                    <Text style={styles.summaryValue}>{targetInstanceCount}</Text>
                </View>
                <View style={[styles.summaryRow, styles.summaryRowTotal]}>
                    <Text style={styles.summaryLabelBold}>Total after merge</Text>
                    <Text style={styles.summaryValueBold}>Depends on time matching</Text>
                </View>
            </View>
        </View>
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case STEP_SELECT:
                return renderSelectStep();
            case STEP_REVIEW:
                return renderReviewStep();
            default:
                return null;
        }
    };

    return (
        <View style={styles.container}>
            {renderStepIndicator()}

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {renderStepContent()}
            </ScrollView>

            <View style={styles.navigationBar}>
                <View style={styles.navButtonContainer}>
                    {currentStep > STEP_SELECT && (
                        <SolidButton
                            text="Back"
                            iconName="arrow-back"
                            iconBefore
                            variant="outlined"
                            onPress={() => setCurrentStep(prev => prev - 1)}
                        />
                    )}
                </View>

                <View style={styles.navButtonContainer}>
                    {currentStep < STEP_REVIEW ? (
                        <SolidButton
                            text="Continue"
                            iconName="arrow-forward"
                            disabled={!canProceed}
                            onPress={() => setCurrentStep(prev => prev + 1)}
                        />
                    ) : (
                        <SolidButton
                            text="Merge Events"
                            iconName="git-merge-outline"
                            iconBefore
                            type="success"
                            onPress={handleMerge}
                            processing={mergeMutation.isPending}
                        />
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }, rt) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
        paddingBottom: Platform.OS === 'ios' && parseInt(Platform.Version, 10) >= 26 && !Platform.isPad && !rt.isLandscape ? 80 : 0,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: 16,
        paddingBottom: 100,
    },
    stepContent: {
        gap: 16,
    },
    stepIndicatorContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 20,
        paddingHorizontal: 16,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    stepItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    stepCircle: (isActive, isCompleted) => ({
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: isCompleted ? colors.success : isActive ? colors.primary : colors.border,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    stepNumber: (isActive) => ({
        color: isActive ? colors.onPrimary : colors.textSecondary,
        fontSize: 12,
        fontFamily: fonts.bold,
    }),
    stepLabel: (isActive) => ({
        marginLeft: 6,
        fontSize: 12,
        color: isActive ? colors.primary : colors.textSecondary,
        fontFamily: isActive ? fonts.bold : fonts.body,
    }),
    stepConnector: (isCompleted) => ({
        width: rt.screen.width > 500 ? 60 : 30,
        height: 2,
        backgroundColor: isCompleted ? colors.success : colors.border,
        marginHorizontal: 8,
    }),
    groupCard: (isSource) => ({
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        marginVertical: 8,
        borderWidth: 2,
        borderColor: isSource ? colors.error : colors.success,
        borderStyle: isSource ? 'dashed' : 'solid',
        boxShadow: shadows.card,
    }),
    groupCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    groupCardIcon: (isSource) => ({
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: isSource ? colors.errorContainer : colors.successContainer,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    groupCardInfo: {
        flex: 1,
    },
    groupName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    groupMeta: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2,
    },
    roleBadge: (isSource) => ({
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        backgroundColor: isSource ? colors.errorContainer : colors.successContainer,
    }),
    roleBadgeText: (isSource) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: isSource ? colors.error : colors.success,
    }),
    groupCardDetails: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 6,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    detailText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    calendarDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
    },
    groupCardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 8,
    },
    groupCardFooterText: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        flex: 1,
    },
    mergeArrow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 4,
    },
    mergeArrowText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    mergeArrowLine: {
        flex: 1,
        height: 2,
        backgroundColor: colors.border,
    },
    mergeArrowCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    reviewSection: {
        marginTop: 8,
    },
    summaryCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
    },
    summaryTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.text,
        marginBottom: 12,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
    },
    summaryRowTotal: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        marginTop: 4,
        paddingTop: 12,
    },
    summaryLabel: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    summaryValue: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryLabelBold: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    summaryValueBold: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    navigationBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        boxShadow: shadows.card,
    },
    navButtonContainer: {
        minWidth: 100,
    },
    loadingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 12,
    },
    loadingText: {
        fontSize: 14,
        color: colors.textSecondary,
    },
}));
